import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, GripVertical, Phone, MapPin, X, AlertTriangle, BarChart3, Filter, Calendar, CalendarDays, Users, ChevronDown, Target, Save, Trash2, Sparkles, AlertCircle, Home, CheckSquare, UserPlus, Tag, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { FollowUpDialog, FOLLOW_UP_STAGES } from '@/components/FollowUpDialog';
import { FollowUpMissingBanner } from '@/components/FollowUpMissingBanner';

const STAGE_CAPACITY = { lead_nuevo: 50, contactado: 40, calificado: 60, en_visitas: 30, en_negociacion: 20 };

const STAGES = [
  { key: 'lead_nuevo', label: 'Lead Nuevo', color: '#3B82F6' },
  { key: 'contactado', label: 'Contactado', color: '#8B5CF6' },
  { key: 'calificado', label: 'Calificado', color: '#F59E0B' },
  { key: 'en_visitas', label: 'En Visitas', color: '#10B981' },
  { key: 'en_negociacion', label: 'En Negociacion', color: '#EF4444' },
  { key: 'cerrado', label: 'Cerrado', color: '#6BCB77' },
  { key: 'perdido', label: 'Perdido', color: '#6B7280' },
  { key: 'propietario', label: 'Propietarios', color: '#A38560' },
];

const LEAD_SOURCES = ["Pauta", "Referido", "RRSS Sebastian", "RRSS propias", "Meta Ads", "Pagina Web", "WhatsApp", "Llamada", "Otras"];
const LOST_REASONS = ["Duplicado","No contesta","Descartado (legal, presupuesto, desiste de comprar)","Error en el numero","Cerró por otro lado"];
const CLIENT_TYPES = ["Comprador", "Arrendatario", "Propietario"];
const PROPERTY_TYPES = ["Casa", "Apartamento", "Local", "Oficina", "Lote", "PH", "Otro"];

// ==================== LEAD MATCH MODAL ====================
function LeadMatchModal({ open, onClose, sectors, brokers, isAdmin, navigate, inputCls, selectContentCls }) {
  const emptyCriteria = { property_type: '', min_area: 300, soft_area_threshold: 50, min_rooms: 4, sector: '', min_budget: 5000000000, show_all: false };
  const [criteria, setCriteria] = useState(emptyCriteria);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);

  const upd = (k, v) => setCriteria(c => ({ ...c, [k]: v }));

  const loadProfiles = async () => {
    try { const p = await api.get('/lead-match/profiles'); setProfiles(p); } catch { /* ignore */ }
  };

  useEffect(() => { if (open) { loadProfiles(); setResults(null); setCriteria(emptyCriteria); } }, [open]);

  const runMatch = async () => {
    setLoading(true);
    try {
      const payload = {
        property_type: criteria.property_type || '',
        min_area: criteria.min_area === '' ? 0 : parseFloat(criteria.min_area) || 0,
        soft_area_threshold: criteria.soft_area_threshold === '' ? 0 : parseFloat(criteria.soft_area_threshold) || 0,
        min_rooms: criteria.min_rooms === '' ? 0 : parseInt(criteria.min_rooms) || 0,
        sector: criteria.sector || '',
        min_budget: criteria.min_budget === '' ? 0 : parseFloat(criteria.min_budget) || 0,
        show_all: !!criteria.show_all,
      };
      const r = await api.post('/crm/leads/match', payload);
      setResults(r);
      if (r.count === 0) toast.info('Ningún lead cumple los criterios');
    } catch (e) { toast.error('Error al emparejar'); }
    setLoading(false);
  };

  const saveProfile = async () => {
    if (!profileName.trim()) return toast.error('Nombre del perfil requerido');
    try {
      await api.post('/lead-match/profiles', { name: profileName.trim(), criteria });
      toast.success('Perfil guardado');
      setProfileName(''); setSaveOpen(false);
      loadProfiles();
    } catch { toast.error('Error al guardar'); }
  };

  const loadProfile = (p) => {
    setCriteria({ ...emptyCriteria, ...(p.criteria || {}) });
    toast.success(`Perfil "${p.name}" cargado`);
  };

  const deleteProfile = async (id) => {
    if (!window.confirm('¿Eliminar este perfil?')) return;
    try { await api.del(`/lead-match/profiles/${id}`); toast.success('Perfil eliminado'); loadProfiles(); }
    catch { toast.error('Error'); }
  };

  const resetFilters = () => { setCriteria(emptyCriteria); setResults(null); };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0A1F1A] border-[#1A3B32] text-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="match-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/5">
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <Target size={20} className="text-[#A38560]" /> Emparejar Leads
          </DialogTitle>
          <p className="text-xs text-white/50 mt-1">Encuentra leads que coincidan con una propiedad. Presupuesto y habitaciones son estrictos; área y sector son flexibles.</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Saved profiles */}
          {profiles.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Perfiles guardados</p>
              <div className="flex flex-wrap gap-1.5">
                {profiles.map(p => (
                  <div key={p.id} className="group inline-flex items-center gap-1 bg-[#16302B] border border-white/10 rounded-lg pl-2.5 pr-1 py-1" data-testid={`profile-chip-${p.id}`}>
                    <button onClick={() => loadProfile(p)} className="text-xs text-white/80 hover:text-[#A38560]">{p.name}</button>
                    <button onClick={() => deleteProfile(p.id)} className="text-white/20 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={10} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-white/50 text-[10px] uppercase tracking-widest mb-1 block flex items-center gap-1"><Home size={10} /> Tipo de propiedad</Label>
              <Select value={criteria.property_type || '_any'} onValueChange={v => upd('property_type', v === '_any' ? '' : v)}>
                <SelectTrigger className={inputCls} data-testid="match-property-type"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="_any">Cualquiera</SelectItem>
                  {PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/50 text-[10px] uppercase tracking-widest mb-1 block">Área mínima (m²) <span className="text-[#A38560]">suave</span></Label>
              <Input type="number" value={criteria.min_area} onChange={e => upd('min_area', e.target.value)} className={inputCls} data-testid="match-min-area" placeholder="Ej: 300" />
            </div>
            <div>
              <Label className="text-white/50 text-[10px] uppercase tracking-widest mb-1 block">Tolerancia área (m²)</Label>
              <Input type="number" value={criteria.soft_area_threshold} onChange={e => upd('soft_area_threshold', e.target.value)} className={inputCls} data-testid="match-soft-threshold" placeholder="Ej: 50" />
            </div>
            <div>
              <Label className="text-white/50 text-[10px] uppercase tracking-widest mb-1 block">Habitaciones ≥ <span className="text-red-400/70">estricto</span></Label>
              <Input type="number" value={criteria.min_rooms} onChange={e => upd('min_rooms', e.target.value)} className={inputCls} data-testid="match-min-rooms" placeholder="Ej: 4" />
            </div>
            <div>
              <Label className="text-white/50 text-[10px] uppercase tracking-widest mb-1 block">Sector <span className="text-[#A38560]">suave</span></Label>
              <Select value={criteria.sector || '_any'} onValueChange={v => upd('sector', v === '_any' ? '' : v)}>
                <SelectTrigger className={inputCls} data-testid="match-sector"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="_any">Cualquiera</SelectItem>
                  {sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/50 text-[10px] uppercase tracking-widest mb-1 block">Presupuesto mínimo (COP) <span className="text-red-400/70">estricto</span></Label>
              <Input type="number" value={criteria.min_budget} onChange={e => upd('min_budget', e.target.value)} className={inputCls} data-testid="match-min-budget" placeholder="Ej: 5000000000" />
              {criteria.min_budget > 0 && <p className="text-[10px] text-[#A38560] mt-1 tabular-nums">{formatCOP(criteria.min_budget)}</p>}
            </div>
          </div>

          {isAdmin && (
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer" data-testid="match-show-all">
              <input type="checkbox" checked={!!criteria.show_all} onChange={e => upd('show_all', e.target.checked)} className="w-4 h-4 accent-[#A38560]" />
              Buscar en leads de todo el equipo (no solo los míos)
            </label>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
            <button onClick={runMatch} disabled={loading} data-testid="match-run-btn"
              className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-5 py-2.5 rounded-xl text-sm disabled:opacity-50">
              <Sparkles size={14} /> {loading ? 'Buscando...' : 'Buscar coincidencias'}
            </button>
            <button onClick={resetFilters} className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-white text-sm">Limpiar</button>
            <div className="ml-auto flex gap-2">
              {!saveOpen ? (
                <button onClick={() => setSaveOpen(true)} data-testid="save-profile-btn" className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-[#A38560] text-sm">
                  <Save size={14} /> Guardar perfil
                </button>
              ) : (
                <div className="flex gap-1.5">
                  <Input value={profileName} onChange={e => setProfileName(e.target.value)} placeholder="Nombre del perfil..." className={`${inputCls} w-52`} data-testid="profile-name-input" onKeyDown={e => e.key === 'Enter' && saveProfile()} autoFocus />
                  <button onClick={saveProfile} data-testid="save-profile-confirm" className="px-3 rounded-xl bg-[#A38560] text-[#03110D] font-semibold text-sm">Guardar</button>
                  <button onClick={() => { setSaveOpen(false); setProfileName(''); }} className="px-3 rounded-xl bg-white/5 text-white/40"><X size={14} /></button>
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          {results && (
            <div className="pt-2" data-testid="match-results">
              {/* Other brokers with matching leads (privacy-safe aggregation) */}
              {results.other_brokers && results.other_brokers.length > 0 && (
                <div className="mb-4 p-3 rounded-xl bg-[#16302B] border border-white/10" data-testid="match-other-brokers">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={12} className="text-[#A38560]" />
                    <p className="text-[10px] uppercase tracking-widest text-white/50">Otros brokers con coincidencias</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {results.other_brokers.map(b => (
                      <div key={b.broker_id} data-testid={`other-broker-${b.broker_id}`}
                        className="inline-flex items-center gap-2 px-2.5 py-1 bg-[#0A1F1A] border border-white/10 rounded-lg">
                        <span className="text-xs text-white/80">{b.broker_name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#A38560]/15 text-[#A38560] font-semibold tabular-nums">{b.count}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-white/30 mt-2 italic">Solo se muestra el total, no los datos de los leads.</p>
                </div>
              )}
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-white/50">
                  <span className="text-white font-medium">{results.count}</span> lead{results.count !== 1 ? 's' : ''} encontrado{results.count !== 1 ? 's' : ''}
                  {results.leads?.some(l => l._match_perfect) && (
                    <span className="ml-2 text-[#6BCB77]">· {results.leads.filter(l => l._match_perfect).length} match{results.leads.filter(l => l._match_perfect).length !== 1 ? 'es' : ''} perfecto{results.leads.filter(l => l._match_perfect).length !== 1 ? 's' : ''}</span>
                  )}
                </p>
              </div>
              {results.count === 0 ? (
                <div className="text-center py-10 bg-[#16302B]/40 rounded-xl border border-white/5">
                  <AlertCircle size={28} className="text-white/20 mx-auto mb-2" />
                  <p className="text-white/40 text-sm">Sin coincidencias con estos criterios</p>
                  <p className="text-white/25 text-xs mt-1">Prueba aumentar la tolerancia de área o quitar el filtro de sector.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {results.leads.map(l => (
                    <div key={l.id} onClick={() => { onClose(); navigate(`/crm/${l.id}`); }}
                      data-testid={`match-lead-${l.id}`}
                      className={`p-3 rounded-xl cursor-pointer border transition-all group ${l._match_perfect ? 'bg-[#6BCB77]/[0.04] border-[#6BCB77]/20 hover:border-[#6BCB77]/40' : 'bg-[#16302B] border-white/5 hover:border-[#A38560]/30'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="text-sm font-medium text-white truncate">{l.full_name}</p>
                            {l._match_perfect && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#6BCB77]/15 text-[#6BCB77] font-semibold uppercase tracking-wider">Match perfecto</span>}
                            {(l._match_flags || []).map(f => (
                              <span key={f.key} className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 font-medium">
                                <AlertTriangle size={9} /> {f.label}
                              </span>
                            ))}
                          </div>
                          <div className="flex items-center gap-3 flex-wrap text-[11px] text-white/50">
                            {l.property_type && <span>🏠 {l.property_type}</span>}
                            {l.budget > 0 && <span className="text-[#A38560]">{formatCOP(l.budget)}</span>}
                            {l.rooms > 0 && <span>{l.rooms} hab</span>}
                            {l.min_area > 0 && <span>≥{l.min_area}m²</span>}
                            {l.sector && <span className="flex items-center gap-0.5"><MapPin size={10} /> {l.sector}</span>}
                          </div>
                          {l.assigned_broker_name && <p className="text-[10px] text-white/30 mt-1">Broker: {l.assigned_broker_name}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/50">{STAGES.find(s => s.key === l.stage)?.label || l.stage}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CRM() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // === Persisted CRM filters (localStorage, per-user) ===
  const LS_KEY = user?.id ? `selecto_crm_state_v1_${user.id}` : 'selecto_crm_state_v1_anon';
  const readLS = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; } };
  const initial = readLS();

  const [leads, setLeads] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [allSources, setAllSources] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState(initial.view || 'kanban');
  const [form, setForm] = useState({ full_name: '', phone: '', email: '', budget: '', sector: '', sectors: [], rooms: '', min_area: '', property_type: '', other_characteristics: '', source: 'Otras', client_type: '', assigned_broker_id: '', assigned_broker_name: '', notes: '', custom_field_values: {} });
  const [newSector, setNewSector] = useState('');
  const [showNewSector, setShowNewSector] = useState(false);
  const [lostModal, setLostModal] = useState(null);
  const [lostReason, setLostReason] = useState('');
  const [filterOpen, setFilterOpen] = useState(!!initial.filterOpen);
  const [filterStartDate, setFilterStartDate] = useState(initial.filterStartDate || '');
  const [filterEndDate, setFilterEndDate] = useState(initial.filterEndDate || '');
  const [filterSource, setFilterSource] = useState(initial.filterSource || '');
  const [filterBroker, setFilterBroker] = useState(initial.filterBroker || '');
  const [filterClientType, setFilterClientType] = useState(initial.filterClientType || '');
  const [filterTagId, setFilterTagId] = useState(initial.filterTagId || '');
  const [showAll, setShowAll] = useState(isAdmin ? !!initial.showAll : false);
  const [showMatch, setShowMatch] = useState(false);
  // Bulk select + assign
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null); // { source: 'select'|'filter', ids: [], count: number }
  const [bulkBrokerId, setBulkBrokerId] = useState('');
  // Unassigned alert
  const [unassignedList, setUnassignedList] = useState([]);
  const [unassignedOpen, setUnassignedOpen] = useState(false);
  const hasFilters = filterStartDate || filterEndDate || filterSource || filterBroker || filterClientType || filterTagId;

  // Persist filters/view/showAll on change
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        view, filterOpen, filterStartDate, filterEndDate, filterSource, filterBroker, filterClientType, filterTagId, showAll,
      }));
    } catch { /* quota */ }
  }, [LS_KEY, view, filterOpen, filterStartDate, filterEndDate, filterSource, filterBroker, filterClientType, filterTagId, showAll]);

  const clearFilters = () => {
    setFilterStartDate(''); setFilterEndDate(''); setFilterSource(''); setFilterBroker(''); setFilterClientType(''); setFilterTagId('');
    setShowAll(false);
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
    toast.success('Filtros restablecidos');
  };

  const createSector = async () => {
    const name = newSector.trim();
    if (!name) return;
    try { await api.post('/sectors', { name }); setSectors(prev => [...prev, name].sort()); setForm({ ...form, sector: name }); setNewSector(''); setShowNewSector(false); toast.success('Sector creado'); }
    catch { toast.error('Error al crear sector'); }
  };

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStartDate) params.set('start_date', filterStartDate);
      if (filterEndDate) params.set('end_date', filterEndDate);
      if (filterSource) params.set('source', filterSource);
      if (filterBroker) params.set('broker_id', filterBroker);
      if (filterClientType) params.set('client_type', filterClientType);
      if (showAll) params.set('show_all', 'true');
      const qs = params.toString();
      const [l, b, s, src] = await Promise.all([api.get(`/crm/leads${qs ? '?' + qs : ''}`), api.get('/users/brokers'), api.get('/sectors'), api.get('/crm/sources')]);
      setLeads(l); setBrokers(b); setSectors(s); setAllSources(src);
    } catch { toast.error('Error cargando CRM'); }
  }, [filterStartDate, filterEndDate, filterSource, filterBroker, filterClientType, showAll]);

  const loadUnassigned = useCallback(async () => {
    if (!isAdmin) return;
    try { const r = await api.get('/crm/leads/unassigned'); setUnassignedList(r.leads || []); }
    catch { /* ignore */ }
  }, [isAdmin]);

  useEffect(() => { load(); loadUnassigned(); const iv = setInterval(() => { load(); loadUnassigned(); }, 30000); return () => clearInterval(iv); }, [load, loadUnassigned]);

  // Aggregate all tags currently visible for the tag filter dropdown
  const availableTags = (() => {
    const map = new Map();
    (leads || []).forEach(l => (l.tags || []).forEach(t => { if (t?.id && !map.has(t.id)) map.set(t.id, t); }));
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const filtered = leads.filter(l => {
    if (search && !(l.full_name?.toLowerCase().includes(search.toLowerCase()) || l.phone?.includes(search))) return false;
    if (filterTagId && !(l.tags || []).some(t => t.id === filterTagId)) return false;
    return true;
  });
  const uniqueSources = [...new Set(leads.map(l => l.source).filter(Boolean))].sort();

  // === Bulk-assign helpers ===
  const toggleSelect = (leadId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };
  const openBulkFromSelection = () => {
    if (selectedIds.size === 0) return toast.error('Selecciona al menos un lead');
    setBulkBrokerId(''); setBulkModal({ source: 'select', ids: Array.from(selectedIds), count: selectedIds.size });
  };
  const openBulkFromFilter = () => {
    if (filtered.length === 0) return toast.error('No hay leads que asignar');
    setBulkBrokerId(''); setBulkModal({ source: 'filter', ids: filtered.map(l => l.id), count: filtered.length });
  };
  const openBulkFromUnassigned = (ids) => {
    if (!ids || ids.length === 0) return;
    setBulkBrokerId(''); setBulkModal({ source: 'unassigned', ids, count: ids.length });
  };
  const runBulkAssign = async () => {
    if (!bulkBrokerId) return toast.error('Selecciona un broker');
    try {
      const r = await api.post('/crm/leads/bulk-assign', { lead_ids: bulkModal.ids, broker_id: bulkBrokerId });
      toast.success(`${r.assigned} lead(s) asignados a ${r.broker_name}`);
      setBulkModal(null); setBulkBrokerId(''); exitSelectMode();
      load(); loadUnassigned();
      window.dispatchEvent(new Event('selecto:unassigned-refresh'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al asignar'); }
  };

  const onDragStart = (e, lead) => { e.dataTransfer.setData('leadId', lead.id); e.dataTransfer.setData('fromStage', lead.stage); };

  const [followUpLead, setFollowUpLead] = useState(null);
  const [followUpTick, setFollowUpTick] = useState(0);
  const onDrop = async (e, targetStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    const fromStage = e.dataTransfer.getData('fromStage');
    if (fromStage === targetStage) return;
    if (targetStage === 'perdido') {
      setLostModal({ leadId, fromStage });
      setLostReason('');
      return;
    }
    try {
      const updated = await api.put(`/crm/leads/${leadId}/stage`, { stage: targetStage }); load();
      if (FOLLOW_UP_STAGES.includes(targetStage)) setFollowUpLead(updated);
    }
    catch { toast.error('Error al mover lead'); }
  };

  const confirmLost = async () => {
    if (!lostReason) return toast.error('Selecciona un motivo');
    try {
      await api.put(`/crm/leads/${lostModal.leadId}/stage`, { stage: 'perdido', lost_reason: lostReason });
      setLostModal(null); load(); toast.success('Lead movido a Perdido');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const createLead = async () => {
    if (!form.full_name.trim()) return toast.error('Nombre requerido');
    try {
      const payload = { ...form };
      if (payload.budget === '' || payload.budget === null) payload.budget = undefined;
      if (payload.rooms === '' || payload.rooms === null) payload.rooms = undefined;
      if (payload.min_area === '' || payload.min_area === null) payload.min_area = undefined;
      if (!payload.property_type) payload.property_type = undefined;
      if (!payload.assigned_broker_id) { payload.assigned_broker_id = undefined; payload.assigned_broker_name = undefined; }
      if (!payload.client_type) payload.client_type = undefined;
      // Merge sectors array into sector field for backward compat
      if (payload.sectors && payload.sectors.length > 0) { payload.sector = payload.sectors.join(', '); }
      await api.post('/crm/leads', payload);
      toast.success('Lead creado');
      setShowModal(false);
      setForm({ full_name: '', phone: '', email: '', budget: '', sector: '', sectors: [], rooms: '', min_area: '', property_type: '', other_characteristics: '', source: 'Otras', client_type: '', assigned_broker_id: '', assigned_broker_name: '', notes: '', custom_field_values: {} });
      load();
    } catch { toast.error('Error al crear lead'); }
  };

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] h-11 rounded-xl";
  const selectContentCls = "bg-[#0A1F1A] border-[#1A3B32] text-white";

  const AttemptDots = ({ lead }) => {
    const maxAtt = lead.max_attempts || 0;
    const count = lead.attempt_count || 0;
    if (maxAtt === 0) return null;
    return (
      <div className="flex items-center gap-0.5 mt-1.5">
        {Array.from({ length: maxAtt }).map((_, i) => (
          <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < count ? 'bg-red-400' : 'bg-white/10'}`} />
        ))}
        {count > 0 && <span className="text-[10px] text-red-400/60 ml-1">{count}/{maxAtt}</span>}
      </div>
    );
  };

  const claimPoolLead = async (leadId) => {
    try {
      await api.post(`/crm/leads/${leadId}/claim`);
      toast.success('Lead reclamado — ahora es tuyo');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo reclamar';
      toast.error(msg);
      load(); // Refresh so it disappears from the pool
    }
  };

  const poolHoursLeft = (deadline) => {
    if (!deadline) return null;
    const diff = new Date(deadline) - new Date();
    return Math.max(0, Math.ceil(diff / 3600000));
  };

  const LeadCard = ({ lead }) => {
    const allUsed = (lead.max_attempts || 0) > 0 && (lead.attempt_count || 0) >= (lead.max_attempts || 0);
    const isRed = lead.health === 'red';
    const isYellow = lead.health === 'yellow';
    const fuLabel = lead.next_follow_up_date ? new Date(lead.next_follow_up_date + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short', ...(lead.next_follow_up_date.slice(0, 4) !== String(new Date().getFullYear()) ? { year: 'numeric' } : {}) }) + (lead.next_follow_up_time ? ` ${lead.next_follow_up_time}` : '') : '';
    const isPool = lead.is_pool && !lead.pool_claimed_by;
    const hoursLeft = (lead.is_pool && lead.pool_claimed_by && !lead.pool_attended) ? poolHoursLeft(lead.pool_deadline_at) : null;
    const isSelected = selectedIds.has(lead.id);
    const [showActs, setShowActs] = useState(false);
    const recentActs = (lead.activity_log || []).slice(-5).reverse();
    return (
      <div draggable={!selectMode && !isPool} onDragStart={e => !selectMode && !isPool && onDragStart(e, lead)}
        data-testid={`lead-card-${lead.id}`}
        className={`rounded-xl p-3 transition-all group relative overflow-hidden ${
          selectMode && isSelected ? 'bg-[#A38560]/10 border-2 border-[#A38560]' :
          isPool ? 'bg-amber-500/[0.08] border border-amber-500/40 hover:border-amber-400/70 shadow-[0_0_16px_-4px_rgba(245,158,11,0.35)]' :
          isRed ? 'bg-red-500/[0.06] border border-red-500/20 hover:border-red-400/40' :
          isYellow ? 'bg-amber-400/[0.06] border border-amber-400/25 hover:border-amber-300/50' :
          'bg-[#16302B] border border-white/5 hover:border-[#A38560]/30'
        }`}>
        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${isPool ? 'bg-amber-400' : isRed ? 'bg-red-500' : isYellow ? 'bg-yellow-400' : 'bg-green-500'}`} />
        <div className={selectMode ? 'pl-2' : 'pl-2 cursor-pointer'}
          onClick={() => selectMode ? toggleSelect(lead.id) : navigate(`/crm/${lead.id}`)}>
          {isPool && (
            <div data-testid={`pool-badge-${lead.id}`} className="flex items-center gap-1 mb-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
              <span className="text-[9px] text-amber-300 font-bold uppercase tracking-wider">Disponible para reclamar</span>
            </div>
          )}
          {hoursLeft !== null && (
            <div data-testid={`pool-deadline-${lead.id}`} className="flex items-center gap-1 mb-1.5">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${hoursLeft <= 12 ? 'bg-red-400 animate-pulse' : 'bg-amber-400'}`} />
              <span className={`text-[9px] font-bold uppercase tracking-wider ${hoursLeft <= 12 ? 'text-red-300' : 'text-amber-300'}`}>
                Reclamado · vuelve al pool en {hoursLeft}h
              </span>
            </div>
          )}
          <div className="flex items-start justify-between mb-1">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selectMode && (
                <input type="checkbox" readOnly checked={isSelected}
                  data-testid={`select-lead-${lead.id}`}
                  className="w-4 h-4 accent-[#A38560] shrink-0 pointer-events-none" />
              )}
              <p className={`text-sm font-medium truncate ${isPool ? 'text-amber-100' : isRed ? 'text-red-300' : 'text-white'}`}>{lead.full_name}</p>
            </div>
            {!selectMode && !isPool && <GripVertical size={14} className="text-white/20 group-hover:text-white/40 shrink-0 ml-1" />}
          </div>
          {isRed && (
            <div className="flex items-center gap-1 mb-1">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" />
              <span className="text-[8px] text-red-400 font-semibold uppercase tracking-wider">{lead.stage === 'lead_nuevo' ? 'Sin contactar' : 'Seguimiento vencido'}</span>
            </div>
          )}
          {isYellow && (
            <div className="flex items-center gap-1 mb-1" data-testid={`follow-up-today-${lead.id}`}>
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" />
              <span className="text-[8px] text-yellow-300 font-semibold uppercase tracking-wider">Seguimiento hoy</span>
            </div>
          )}
          {lead.needs_follow_up && (
            <div className="flex items-center gap-1 mb-1" data-testid={`no-follow-up-${lead.id}`}>
              <AlertTriangle size={9} className="text-amber-400 shrink-0" />
              <span className="text-[8px] text-amber-300 font-semibold uppercase tracking-wider">Sin fecha de seguimiento</span>
            </div>
          )}
          {fuLabel && FOLLOW_UP_STAGES.includes(lead.stage) && <p className="text-[10px] text-white/40 flex items-center gap-1 mb-1" data-testid={`follow-up-date-${lead.id}`}><Clock size={10} />Seguimiento: {fuLabel}</p>}
          {lead.phone && <p className="text-xs text-white/50 flex items-center gap-1 mb-1"><Phone size={10} />{lead.phone}</p>}
          {lead.budget && <p className="text-xs text-[#A38560] font-medium tabular-nums mb-1">{formatCOP(lead.budget)}</p>}
          {lead.sector && <p className="text-xs text-white/40 flex items-center gap-1 mb-1"><MapPin size={10} />{lead.sector}</p>}
          {lead.stage === 'cerrado' && lead.closed_deal_summary && lead.closed_deal_summary.length > 0 && (
            <div className="mt-1.5 mb-1 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/25 p-2 space-y-1" data-testid={`closed-deal-summary-${lead.id}`}>
              {lead.closed_deal_summary.slice(0, 2).map(d => {
                const isRent = (d.deal_type || '').toLowerCase() === 'arriendo';
                const dateTxt = d.close_date
                  ? new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(d.close_date))
                  : '—';
                return (
                  <div key={d.deal_id} className="text-[10px] leading-tight">
                    <p className="text-emerald-300 font-semibold tabular-nums flex items-center gap-1.5">
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 uppercase tracking-wider">
                        {isRent ? 'Arriendo' : 'Venta'}
                      </span>
                      <span>{formatCOP(d.value)}{isRent ? '/mes' : ''}</span>
                    </p>
                    {d.property_address && (
                      <p className="text-white/60 truncate flex items-center gap-1 mt-0.5"><MapPin size={9} className="shrink-0" />{d.property_address}</p>
                    )}
                    <p className="text-white/40 flex items-center gap-1 mt-0.5"><CalendarDays size={9} className="shrink-0" />Cerrado el {dateTxt}</p>
                  </div>
                );
              })}
              {lead.closed_deal_summary.length > 2 && (
                <p className="text-[9px] text-white/40 text-center">+ {lead.closed_deal_summary.length - 2} negocio(s) más</p>
              )}
            </div>
          )}
          {lead.new_match_at && (
            <div className="mb-1.5">
              <span data-testid={`card-new-match-${lead.id}`} className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-[#A38560]/20 text-[#E2C79C] border border-[#A38560]/50 animate-pulse">
                <span className="w-1 h-1 rounded-full bg-[#E2C79C]" />Nueva compatibilidad{lead.new_match_count > 1 ? ` · ${lead.new_match_count}` : ''}
              </span>
            </div>
          )}
          {(lead.tags && lead.tags.length > 0) && (
            <div className="flex flex-wrap gap-1 mb-1.5" data-testid={`card-tags-${lead.id}`}>
              {lead.tags.slice(0, 4).map(t => (
                <span key={t.id} className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: t.color + '22', color: t.color, border: `1px solid ${t.color}44` }}>
                  <span className="w-1 h-1 rounded-full" style={{ backgroundColor: t.color }}></span>{t.name}
                </span>
              ))}
              {lead.tags.length > 4 && <span className="text-[9px] text-white/40">+{lead.tags.length - 4}</span>}
            </div>
          )}
          <AttemptDots lead={lead} />
          {allUsed && (
            <div className="mt-2 py-1.5 px-2 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
              <p className="text-[10px] text-red-400 font-medium">Intentos agotados</p>
            </div>
          )}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/40">{lead.source}</span>
              {lead.client_type && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#A38560]/10 text-[#A38560]">{lead.client_type}</span>}
            </div>
            {lead.assigned_broker_name && <span className="text-[10px] text-white/30 truncate max-w-[80px]">{lead.assigned_broker_name}</span>}
          </div>
          {isPool && (
            <button onClick={(e) => { e.stopPropagation(); claimPoolLead(lead.id); }}
              data-testid={`claim-btn-${lead.id}`}
              className="mt-2 w-full py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs font-semibold border border-amber-500/40 transition-colors">
              Reclamar este lead
            </button>
          )}
        </div>
        {/* Activities expander (not part of the clickable body) */}
        <button onClick={(e) => { e.stopPropagation(); setShowActs(!showActs); }}
          data-testid={`toggle-acts-${lead.id}`}
          className="mt-2 w-full flex items-center justify-between gap-1 pl-2 pr-1 py-1 text-[10px] text-white/40 hover:text-[#A38560] transition-colors border-t border-white/5">
          <span>Ultimas actividades ({(lead.activity_log || []).length})</span>
          <ChevronDown size={11} className={`transition-transform ${showActs ? 'rotate-180' : ''}`} />
        </button>
        {showActs && (
          <div className="pl-2 pr-1 pb-1 space-y-1" data-testid={`acts-list-${lead.id}`}>
            {recentActs.length ? recentActs.map((a, i) => (
              <div key={a.id || i} className="text-[10px] bg-[#0A1F1A]/50 rounded-md px-2 py-1.5 border-l-2" style={{ borderLeftColor: a.type === 'note' ? '#A38560' : '#4ADE80' }}>
                <div className="flex items-center justify-between gap-1 mb-0.5">
                  <span className="text-[#A38560] font-medium">{a.type === 'note' ? '📝 Nota' : '📞 Contacto'}</span>
                  <span className="text-white/30 text-[9px]">{a.created_at ? new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(a.created_at)) : ''}</span>
                </div>
                <p className="text-white/60 line-clamp-2">{a.message || a.details || '(sin texto)'}</p>
              </div>
            )) : <p className="text-[10px] text-white/30 italic py-2 text-center">Sin actividades registradas</p>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 pb-24">
      {/* === Unassigned Leads Alert (Admin only) === */}
      {isAdmin && unassignedList.length > 0 && (
        <div data-testid="unassigned-banner"
          className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-4 lg:p-5">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-500/10 blur-2xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className="text-amber-400" />
              </div>
              <div className="min-w-0">
                <p className="font-outfit text-base font-semibold text-white flex items-center gap-2 flex-wrap">
                  <span data-testid="unassigned-count">{unassignedList.length}</span> lead{unassignedList.length !== 1 ? 's' : ''} sin broker asignado
                  <span className="inline-flex w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                </p>
                <p className="text-xs text-white/60 mt-0.5">Asígnalos ahora para que empiece la gestión de contacto.</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setUnassignedOpen(!unassignedOpen)} data-testid="unassigned-toggle-btn"
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:text-white text-xs transition-colors">
                {unassignedOpen ? 'Ocultar' : 'Ver lista'}
              </button>
              <button onClick={() => openBulkFromUnassigned(unassignedList.map(l => l.id))} data-testid="unassigned-assign-all-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-[#03110D] font-semibold text-xs transition-colors">
                <UserPlus size={13} /> Asignar todos
              </button>
            </div>
          </div>
          {unassignedOpen && (
            <div className="mt-4 pt-4 border-t border-amber-500/20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="unassigned-list">
              {unassignedList.map(l => (
                <div key={l.id} onClick={() => navigate(`/crm/${l.id}`)}
                  data-testid={`unassigned-item-${l.id}`}
                  className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-[#16302B]/60 border border-white/5 hover:border-amber-500/30 cursor-pointer transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{l.full_name}</p>
                    <p className="text-[10px] text-white/40 truncate">{l.source} {l.phone ? ` · ${l.phone}` : ''}</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); openBulkFromUnassigned([l.id]); }}
                    data-testid={`assign-one-${l.id}`}
                    className="shrink-0 px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-medium hover:bg-amber-500/25 transition-colors">
                    Asignar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <FollowUpDialog lead={followUpLead} onDone={() => { setFollowUpLead(null); setFollowUpTick(t => t + 1); load(); }} />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="font-outfit text-2xl font-semibold text-white">CRM Pipeline</h1>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3">
          <div className="relative flex-1 min-w-[180px] sm:flex-initial">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar lead..." className={`${inputCls} pl-9 w-full sm:w-64`} data-testid="crm-search" />
          </div>
          <div className="flex items-center gap-1 bg-[#0A1F1A] rounded-xl border border-[#1A3B32] p-0.5 whitespace-nowrap">
            <button onClick={() => setView('kanban')} className={`px-3 py-2 text-xs rounded-lg transition-colors ${view === 'kanban' ? 'bg-[#A38560] text-[#03110D]' : 'text-white/50 hover:text-white'}`}>Kanban</button>
            <button onClick={() => setView('list')} className={`px-3 py-2 text-xs rounded-lg transition-colors ${view === 'list' ? 'bg-[#A38560] text-[#03110D]' : 'text-white/50 hover:text-white'}`}>Lista</button>
          </div>
          <button onClick={() => navigate('/crm-dashboard')} data-testid="crm-dashboard-btn" className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-white text-sm transition-colors whitespace-nowrap"><BarChart3 size={14} /> Dashboard</button>
          {isAdmin && (
            <button onClick={() => setShowAll(!showAll)} data-testid="crm-show-all-btn"
              className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm transition-colors whitespace-nowrap ${showAll ? 'bg-[#A38560]/10 border-[#A38560]/30 text-[#A38560]' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}>
              <Users size={14} /> {showAll ? 'Todo el equipo' : 'Mis clientes'}
            </button>
          )}
          {isAdmin && (
            <button onClick={() => { if (selectMode) exitSelectMode(); else setSelectMode(true); }} data-testid="crm-select-mode-btn"
              className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm transition-colors whitespace-nowrap ${selectMode ? 'bg-[#A38560]/15 border-[#A38560]/40 text-[#A38560]' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}>
              <CheckSquare size={14} /> {selectMode ? 'Cancelar' : 'Seleccionar'}
            </button>
          )}
          <button onClick={() => setShowMatch(true)} data-testid="crm-match-btn"
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#A38560]/15 to-[#8B6F47]/15 border border-[#A38560]/30 text-[#A38560] hover:from-[#A38560]/25 hover:to-[#8B6F47]/25 text-sm transition-all whitespace-nowrap">
            <Target size={14} /> Emparejar
          </button>
          <button onClick={() => setFilterOpen(!filterOpen)} data-testid="crm-filter-btn" className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm transition-colors whitespace-nowrap ${hasFilters ? 'bg-[#A38560]/10 border-[#A38560]/30 text-[#A38560]' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}>
            <Filter size={14} /> Filtros {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-[#A38560]" />}
          </button>
          <button onClick={() => setShowModal(true)} data-testid="new-lead-btn" className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-4 py-2.5 rounded-xl text-sm"><Plus size={16} /> Nuevo Lead</button>
        </div>
      </div>

      {/* Filter Panel */}
      {filterOpen && (
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 animate-in slide-in-from-top-2" data-testid="crm-filters">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-[#A38560]" />
              <span className="text-xs uppercase tracking-widest text-white/40">Filtros</span>
              <span className="text-[10px] text-white/30">(se recuerdan hasta que los limpies)</span>
            </div>
            {(hasFilters || showAll) && (
              <button onClick={clearFilters} data-testid="filter-clear-btn"
                className="text-[10px] text-red-400 hover:text-red-300">Limpiar filtros</button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-[10px] text-white/30 uppercase mb-1 block">Desde</label>
              <Input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="bg-[#16302B] border-[#1A3B32] text-white h-9 rounded-lg text-xs" data-testid="filter-start" />
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase mb-1 block">Hasta</label>
              <Input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="bg-[#16302B] border-[#1A3B32] text-white h-9 rounded-lg text-xs" data-testid="filter-end" />
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase mb-1 block">Fuente</label>
              <Select value={filterSource} onValueChange={v => setFilterSource(v === '_all' ? '' : v)}>
                <SelectTrigger className="bg-[#16302B] border-[#1A3B32] text-white h-9 rounded-lg text-xs" data-testid="filter-source"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="_all">Todas</SelectItem>
                  {allSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase mb-1 block">Broker</label>
              <Select value={filterBroker} onValueChange={v => setFilterBroker(v === '_all' ? '' : v)}>
                <SelectTrigger className="bg-[#16302B] border-[#1A3B32] text-white h-9 rounded-lg text-xs" data-testid="filter-broker"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="_all">Todos</SelectItem>
                  {brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase mb-1 block">Tipo Cliente</label>
              <Select value={filterClientType} onValueChange={v => setFilterClientType(v === '_all' ? '' : v)}>
                <SelectTrigger className="bg-[#16302B] border-[#1A3B32] text-white h-9 rounded-lg text-xs" data-testid="filter-type"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="_all">Todos</SelectItem>
                  {CLIENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase mb-1 block">Etiqueta</label>
              <Select value={filterTagId} onValueChange={v => setFilterTagId(v === '_all' ? '' : v)}>
                <SelectTrigger className="bg-[#16302B] border-[#1A3B32] text-white h-9 rounded-lg text-xs" data-testid="filter-tag"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="_all">Todas</SelectItem>
                  {availableTags.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 flex-wrap gap-2">
            <p className="text-[10px] text-white/40 tabular-nums">{filtered.length} leads visibles · {leads.length} totales</p>
            {isAdmin && filtered.length > 0 && (
              <button onClick={openBulkFromFilter} data-testid="bulk-from-filter-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#A38560]/15 border border-[#A38560]/30 text-[#A38560] hover:bg-[#A38560]/25 text-xs font-medium transition-colors">
                <UserPlus size={12} /> Asignar los {filtered.length} leads filtrados a...
              </button>
            )}
          </div>
        </div>
      )}

      <FollowUpMissingBanner isAdmin={isAdmin} refreshKey={followUpTick} />

      {view === 'kanban' ? (
        <div className="overflow-x-auto pb-4 -mx-2 px-2">
          <div className="flex gap-3" style={{ minWidth: `${STAGES.length * 260}px` }}>
            {STAGES.map(stage => {
              const stageLeads = filtered.filter(l => l.stage === stage.key);
              const redCount = stageLeads.filter(l => l.health === 'red').length;
              const cap = STAGE_CAPACITY[stage.key];
              const isFull = cap && stageLeads.length >= cap;
              const pct = cap ? Math.min((stageLeads.length / cap) * 100, 100) : 0;
              return (
                <div key={stage.key} data-testid={`kanban-col-${stage.key}`} className="flex-1 min-w-[240px]" onDragOver={e => e.preventDefault()} onDrop={e => onDrop(e, stage.key)}>
                  <div className="flex items-center gap-2 mb-1.5 px-1">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="text-sm font-medium text-white/80">{stage.label}</span>
                    <span className="text-xs text-white/30 ml-auto">{stageLeads.length}{cap ? `/${cap}` : ''}</span>
                    {redCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">{redCount}</span>}
                  </div>
                  {cap && (
                    <div className="h-1 bg-white/5 rounded-full mx-1 mb-2 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: isFull ? '#EF4444' : stage.color }} />
                    </div>
                  )}
                  {isFull && <p className="text-[10px] text-red-400 px-1 mb-1.5 font-medium" data-testid={`cap-full-${stage.key}`}>Capacidad maxima</p>}
                  <div className="space-y-2 min-h-[100px] bg-[#0A1F1A]/30 rounded-xl p-2 border border-white/5">
                    {stageLeads.map(lead => <LeadCard key={lead.id} lead={lead} />)}
                    {stageLeads.length === 0 && <div className="text-center py-8 text-white/20 text-xs">Sin leads</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden">
          <table className="w-full"><thead><tr className="border-b border-white/5">
            <th className="text-left p-3 text-xs text-white/40 uppercase">Nombre</th><th className="text-left p-3 text-xs text-white/40 uppercase">Etapa</th>
            <th className="text-left p-3 text-xs text-white/40 uppercase hidden sm:table-cell">Fuente</th><th className="text-left p-3 text-xs text-white/40 uppercase hidden md:table-cell">Broker</th>
            <th className="text-left p-3 text-xs text-white/40 uppercase hidden lg:table-cell">Salud</th>
          </tr></thead><tbody>
            {filtered.map(l => {
              const stageInfo = STAGES.find(s => s.key === l.stage);
              return (
                <tr key={l.id} onClick={() => navigate(`/crm/${l.id}`)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="p-3"><div><p className="text-sm text-white font-medium">{l.full_name}</p>{l.phone && <p className="text-xs text-white/40">{l.phone}</p>}</div></td>
                  <td className="p-3"><span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: `${stageInfo?.color}20`, color: stageInfo?.color }}>{stageInfo?.label}</span></td>
                  <td className="p-3 hidden sm:table-cell text-sm text-white/50">{l.source}</td>
                  <td className="p-3 hidden md:table-cell text-sm text-white/50">{l.assigned_broker_name || '-'}</td>
                  <td className="p-3 hidden lg:table-cell"><div className={`w-3 h-3 rounded-full ${l.health === 'red' ? 'bg-red-500' : l.health === 'yellow' ? 'bg-yellow-400' : 'bg-green-500'}`} /></td>
                </tr>
              );
            })}
          </tbody></table>
        </div>
      )}

      {/* New Lead Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}><DialogContent className="bg-[#0A1F1A] border-[#1A3B32] text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-outfit text-lg">Nuevo Lead</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Nombre Completo *</Label>
              <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className={inputCls} data-testid="lead-name" placeholder="Nombre..." /></div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Telefono</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="lead-phone" placeholder="+57..." /></div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Email</Label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="email@..." /></div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Tipo de Cliente</Label>
              <Select value={form.client_type} onValueChange={v => setForm({ ...form, client_type: v })}>
                <SelectTrigger className={inputCls} data-testid="lead-client-type"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent className={selectContentCls}>{CLIENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Presupuesto</Label>
              <Input type="number" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} className={inputCls} data-testid="lead-budget" /></div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Habitaciones</Label>
              <Input type="number" value={form.rooms} onChange={e => setForm({ ...form, rooms: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Metraje Minimo (m²)</Label>
              <Input type="number" value={form.min_area} onChange={e => setForm({ ...form, min_area: e.target.value })} className={inputCls} placeholder="Ej: 300" data-testid="lead-min-area-create" /></div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Tipo de Propiedad</Label>
              <Select value={form.property_type} onValueChange={v => setForm({ ...form, property_type: v })}>
                <SelectTrigger className={inputCls} data-testid="lead-property-type-create"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent className={selectContentCls}>{PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Fuente</Label>
              <Select value={form.source} onValueChange={v => setForm({ ...form, source: v })}>
                <SelectTrigger className={inputCls} data-testid="lead-source"><SelectValue /></SelectTrigger>
                <SelectContent className={selectContentCls}>{LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-2"><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Zonas de Interes</Label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(form.sectors || []).map(s => (
                  <span key={s} className="inline-flex items-center gap-1 px-2 py-1 bg-[#A38560]/10 text-[#A38560] text-xs rounded-lg border border-[#A38560]/20">
                    {s} <button type="button" onClick={() => setForm({ ...form, sectors: form.sectors.filter(x => x !== s) })} className="hover:text-white"><X size={10} /></button>
                  </span>
                ))}
              </div>
              {!showNewSector ? (
                <div className="flex gap-1.5">
                  <Select value="" onValueChange={v => { if (v && !(form.sectors || []).includes(v)) setForm({ ...form, sectors: [...(form.sectors || []), v] }); }}>
                    <SelectTrigger className={`${inputCls} flex-1`} data-testid="lead-sector"><SelectValue placeholder="Agregar zona..." /></SelectTrigger>
                    <SelectContent className={selectContentCls}>{sectors.filter(s => !(form.sectors || []).includes(s)).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <button type="button" onClick={() => setShowNewSector(true)} className="h-11 px-2.5 rounded-xl bg-white/5 text-white/40 hover:text-[#A38560] hover:bg-white/10 transition-colors"><Plus size={16} /></button>
                </div>
              ) : (
                <div className="flex gap-1.5">
                  <Input value={newSector} onChange={e => setNewSector(e.target.value)} placeholder="Nuevo sector..." className={`${inputCls} flex-1`} onKeyDown={e => e.key === 'Enter' && createSector()} autoFocus />
                  <button type="button" onClick={createSector} className="h-11 px-2.5 rounded-xl bg-[#A38560] text-[#03110D]"><Plus size={16} /></button>
                  <button type="button" onClick={() => { setShowNewSector(false); setNewSector(''); }} className="h-11 px-2.5 rounded-xl bg-white/5 text-white/40 hover:text-white"><X size={16} /></button>
                </div>
              )}</div>
            <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Broker Asignado</Label>
              <Select value={form.assigned_broker_id} onValueChange={v => { const b = brokers.find(x => x.id === v); setForm({ ...form, assigned_broker_id: v, assigned_broker_name: b?.name || '' }); }}>
                <SelectTrigger className={inputCls} data-testid="lead-broker"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={createLead} data-testid="create-lead-btn" className="bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl text-sm w-full">Crear Lead</button>
        </DialogFooter>
      </DialogContent></Dialog>

      {/* Match / Emparejar Leads Modal */}
      <LeadMatchModal open={showMatch} onClose={() => setShowMatch(false)} sectors={sectors} brokers={brokers} isAdmin={isAdmin} navigate={navigate} inputCls={inputCls} selectContentCls={selectContentCls} />

      {/* Floating selection bar (bulk-assign entry) */}
      {selectMode && (
        <div data-testid="bulk-selection-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-[#0A1F1A]/95 backdrop-blur-md border border-[#A38560]/30 rounded-2xl px-4 py-3 shadow-2xl shadow-black/50">
          <div className="flex items-center gap-2">
            <CheckSquare size={16} className="text-[#A38560]" />
            <span className="text-sm text-white font-medium" data-testid="bulk-selected-count">{selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''} seleccionado{selectedIds.size !== 1 ? 's' : ''}</span>
          </div>
          <div className="h-6 w-px bg-white/10" />
          <button onClick={() => setSelectedIds(new Set(filtered.map(l => l.id)))} data-testid="bulk-select-all"
            className="text-xs text-white/60 hover:text-white transition-colors">Todos ({filtered.length})</button>
          <button onClick={() => setSelectedIds(new Set())}
            className="text-xs text-white/60 hover:text-white transition-colors">Ninguno</button>
          <div className="h-6 w-px bg-white/10" />
          <button onClick={openBulkFromSelection} data-testid="bulk-assign-btn"
            disabled={selectedIds.size === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            <UserPlus size={13} /> Asignar a broker
          </button>
          <button onClick={exitSelectMode} className="text-white/40 hover:text-white p-1"><X size={16} /></button>
        </div>
      )}

      {/* Bulk Assign Modal */}
      <Dialog open={!!bulkModal} onOpenChange={v => { if (!v) { setBulkModal(null); setBulkBrokerId(''); } }}>
        <DialogContent className="bg-[#0A1F1A] border-[#1A3B32] text-white max-w-md" data-testid="bulk-assign-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2">
              <UserPlus size={20} className="text-[#A38560]" /> Asignación masiva
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-xl bg-[#16302B] border border-white/5">
              <p className="text-sm text-white">
                Vas a asignar <span className="font-semibold text-[#A38560]">{bulkModal?.count || 0}</span> lead{(bulkModal?.count || 0) !== 1 ? 's' : ''} a un broker.
              </p>
              <p className="text-[10px] text-white/40 mt-1">
                {bulkModal?.source === 'select' && 'Origen: selección manual en el Kanban'}
                {bulkModal?.source === 'filter' && 'Origen: filtros activos (incluye etiqueta si está seleccionada)'}
                {bulkModal?.source === 'unassigned' && 'Origen: leads sin broker asignado'}
              </p>
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Broker destino *</Label>
              <Select value={bulkBrokerId} onValueChange={setBulkBrokerId}>
                <SelectTrigger className={inputCls} data-testid="bulk-broker-select"><SelectValue placeholder="Selecciona un broker..." /></SelectTrigger>
                <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-white/30 italic">
              Los leads seleccionados cambiarán de broker inmediatamente. El broker recibirá una notificación push.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => { setBulkModal(null); setBulkBrokerId(''); }} className="px-4 py-2.5 rounded-xl border border-white/10 text-white/60 text-sm">Cancelar</button>
            <button onClick={runBulkAssign} data-testid="bulk-assign-confirm"
              disabled={!bulkBrokerId}
              className="bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              Confirmar asignación
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lost Lead Modal */}
      <Dialog open={!!lostModal} onOpenChange={() => setLostModal(null)}><DialogContent className="bg-[#0A1F1A] border-[#1A3B32] text-white max-w-md">
        <DialogHeader><DialogTitle className="font-outfit text-lg flex items-center gap-2"><AlertTriangle size={20} className="text-red-400" /> Mover a Perdido</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-white/60">Este lead se moverá a Perdido. Selecciona el motivo obligatoriamente.</p>
          <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Motivo de Perdida *</Label>
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger className={inputCls} data-testid="lost-reason-select"><SelectValue placeholder="Selecciona un motivo..." /></SelectTrigger>
              <SelectContent className={selectContentCls}>{LOST_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Etapa de Origen</Label>
            <Input value={STAGES.find(s => s.key === lostModal?.fromStage)?.label || ''} className={`${inputCls} opacity-60`} disabled /></div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={() => setLostModal(null)} className="px-4 py-2.5 rounded-xl border border-white/10 text-white/60 text-sm">Cancelar</button>
          <button onClick={confirmLost} data-testid="confirm-lost-btn" className="bg-red-500 text-white font-semibold px-6 py-2.5 rounded-xl text-sm">Confirmar Perdido</button>
        </DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
